import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Badge,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import MicIcon from '@mui/icons-material/Mic';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import LayersIcon from '@mui/icons-material/Layers';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import InfoIcon from '@mui/icons-material/Info';
import UpdateIcon from '@mui/icons-material/Update';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import NewMeetingDialog from '../common/NewMeetingDialog';
import SessionWarning from '../common/SessionWarning';
import ConnectionStatusMenuItem from '../ConnectionStatusMenuItem';
import NotificationCenter from '../notifications/NotificationCenter';
import AuthNotificationListener from '../AuthNotificationListener';
import { primaryGradient, primaryGradientTransparent, smoothTransition, organicClipPath } from '../../theme';

const drawerWidth = 280;
const drawerWidthCollapsed = 64;

const MainLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, logout } = useAuth();
  const { unreadCount, markAllAsRead, deleteAllNotifications } = useNotifications();

  // Détecter si on est sur la homepage
  const isHomePage = location.pathname === '/';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(
    () => JSON.parse(localStorage.getItem('drawerCollapsed') || 'false')
  );
  
  // Calculer la largeur actuelle du drawer
  const currentDrawerWidth = desktopCollapsed ? drawerWidthCollapsed : drawerWidth;
  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newMeetingDialogOpen, setNewMeetingDialogOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [notificationsVisible, setNotificationsVisible] = useState(true);
  
  // Ref pour tracker si un timer d'auto-hide est actif
  const autoHideTimerRef = useRef(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDesktopDrawerToggle = () => {
    const newState = !desktopCollapsed;
    setDesktopCollapsed(newState);
    localStorage.setItem('drawerCollapsed', JSON.stringify(newState));
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      window.location.href = '/login';
    } catch (error) {

      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer le nom d'utilisateur à afficher
  const getUserDisplayName = () => {
    if (!currentUser) return 'Utilisateur';
    
    if (currentUser.displayName) {
      return currentUser.displayName;
    }
    
    if (currentUser.profile && currentUser.profile.displayName) {
      return currentUser.profile.displayName;
    }
    
    if (currentUser.email) {
      return currentUser.email.split('@')[0];
    }
    
    return 'Utilisateur';
  };
  
  // Récupérer l'avatar de l'utilisateur
  const getUserAvatar = () => {
    const photoURL = currentUser?.photoURL || 
                     (currentUser?.profile && currentUser.profile.photoURL);
    
    if (photoURL) {
      return <Avatar src={photoURL} sx={{ width: 32, height: 32 }} />;
    }
    
    // Génère une lettre pour l'avatar à partir du nom
    const displayName = getUserDisplayName();
    const initial = displayName.charAt(0).toUpperCase();
    
    return (
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.dark' }}>
        {initial}
      </Avatar>
    );
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Ouvrir le dialog de création de réunion
  const handleNewMeeting = () => {
    setNewMeetingDialogOpen(true);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  // Callback de succès de création de réunion
  const handleNewMeetingSuccess = (meetingId) => {
    setNewMeetingDialogOpen(false);
    // Naviguer vers la liste des réunions
    navigate('/meetings');
  };

  // Toggle des notifications
  const handleNotificationsToggle = () => {
    // Annuler le timer automatique si l'utilisateur clique manuellement
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
    setNotificationsVisible(!notificationsVisible);
  };

  // Supprimer toutes les notifications
  const handleDeleteAllNotifications = async () => {
    if (unreadCount === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer toutes les ${unreadCount} notification${unreadCount > 1 ? 's' : ''} ?`)) {
      try {
        // Annuler le timer automatique
        if (autoHideTimerRef.current) {
          clearTimeout(autoHideTimerRef.current);
          autoHideTimerRef.current = null;
        }
        await deleteAllNotifications();
        // Masquer les notifications puisqu'elles sont supprimées
        setNotificationsVisible(false);
      } catch (error) {
        console.error('Erreur lors de la suppression des notifications:', error);
      }
    }
  };

  // Auto-affichage des notifications quand il y en a de nouvelles + masquage automatique après 2 secondes
  useEffect(() => {
    if (unreadCount > 0 && !notificationsVisible) {
      // Afficher les notifications
      setNotificationsVisible(true);
      
      // Démarrer le timer seulement s'il n'y en a pas déjà un actif
      if (!autoHideTimerRef.current) {
        autoHideTimerRef.current = setTimeout(() => {
          setNotificationsVisible(false);
          autoHideTimerRef.current = null;
        }, 2000);
      }
    } else if (unreadCount > 0 && notificationsVisible && !autoHideTimerRef.current) {
      // Si les notifications sont déjà visibles et qu'il n'y a pas de timer actif, démarrer le timer
      autoHideTimerRef.current = setTimeout(() => {
        setNotificationsVisible(false);
        autoHideTimerRef.current = null;
      }, 2000);
    }

    // Nettoyage du timer si unreadCount devient 0
    if (unreadCount === 0 && autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, [unreadCount]); // Ne pas inclure notificationsVisible pour éviter les boucles

  const isAdmin = currentUser && (currentUser.profile?.isAdmin || currentUser.isAdmin);
  const menuItems = [
    { text: 'Accueil', icon: <DashboardIcon />, path: '/', color: '#FF6B6B' },
    { text: 'Mes réunions', icon: <MicIcon />, path: '/meetings', color: '#5C6BC0' },
    { text: 'Modèles', icon: <LayersIcon />, path: '/models', color: '#F7B731' },
    ...(isAdmin ? [{ text: 'Administration', icon: <SettingsIcon />, path: '/admin', color: '#5F27CD' }] : []),
    { text: 'À propos', icon: <InfoIcon />, path: '/about', color: '#26A69A' },
  ];

  // Fonction pour déterminer si un item est sélectionné
  const isItemSelected = (item) => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    if (item.path === '/models') {
      return location.pathname === '/models' && !location.pathname.startsWith('/models/');
    }
    return location.pathname.startsWith(item.path);
  };

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.98) 100%)',
        backdropFilter: 'blur(20px)',
        border: 'none',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(145deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)',
          opacity: 0.03,
          zIndex: 0
        }
      }}
    >
      {/* Header avec logo et bouton de collapse */}
      <Toolbar sx={{ 
        justifyContent: desktopCollapsed ? 'center' : 'space-between', 
        position: 'relative', 
        zIndex: 1,
        px: desktopCollapsed ? 1 : 2
      }}>
        {!desktopCollapsed && (
          <Box
            component="img"
            src="/logotranscription.png"
            alt="Logo Transcription"
            sx={{ 
              height: 150, 
              width: 'auto', 
              mb: 1, 
              cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              filter: 'drop-shadow(0 4px 8px rgba(63, 81, 181, 0.1))',
              '&:hover': {
                transform: 'scale(1.05)',
                filter: 'drop-shadow(0 8px 16px rgba(63, 81, 181, 0.2))'
              }
            }}
            onClick={() => handleNavigate('/')}
          />
        )}
        
        {/* Bouton de collapse - visible seulement en mode desktop */}
        <IconButton
          onClick={handleDesktopDrawerToggle}
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: desktopCollapsed ? 'static' : 'absolute',
            top: desktopCollapsed ? 'auto' : '16px',
            right: desktopCollapsed ? 'auto' : '16px',
            color: 'rgba(63, 81, 181, 0.7)',
            background: 'rgba(255,255,255,0.8)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(63, 81, 181, 0.1)',
            '&:hover': {
              background: 'rgba(63, 81, 181, 0.1)',
              color: '#3F51B5',
              transform: 'scale(1.1)'
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          {desktopCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>
      
      <Divider sx={{ 
        borderColor: 'rgba(63, 81, 181, 0.1)',
        boxShadow: '0 1px 3px rgba(63, 81, 181, 0.1)'
      }} />
      
      <List sx={{ 
        px: desktopCollapsed ? 0.5 : 2, 
        py: 1, 
        position: 'relative', 
        zIndex: 1 
      }}>
        {menuItems.map((item, index) => {
          const isSelected = isItemSelected(item);
          
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <Tooltip 
                title={desktopCollapsed ? item.text : ""}
                placement="right"
                disableHoverListener={!desktopCollapsed}
              >
                <ListItemButton 
                  onClick={() => handleNavigate(item.path)}
                  onMouseEnter={() => setHoveredItem(index)}
                  onMouseLeave={() => setHoveredItem(null)}
                  selected={isSelected}
                  sx={{ 
                    borderRadius: 3,
                    mx: desktopCollapsed ? 0.5 : 0.5, 
                    my: 0.3,
                    minHeight: 48,
                    justifyContent: desktopCollapsed ? 'center' : 'flex-start',
                    px: desktopCollapsed ? 1 : 2,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: isSelected 
                        ? 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)'
                        : hoveredItem === index 
                          ? 'linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(92, 107, 192, 0.12) 100%)'
                          : 'transparent',
                      borderRadius: 3,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 0
                    },
                    '&::after': desktopCollapsed ? {} : {
                      content: '""',
                      position: 'absolute',
                      top: '50%',
                      left: hoveredItem === index ? '8px' : '-100px',
                      width: '3px',
                      height: '60%',
                      background: item.color,
                      borderRadius: '2px',
                      transform: 'translateY(-50%)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      zIndex: 2,
                      boxShadow: `0 0 10px ${item.color}40`
                    },
                    '&.Mui-selected': {
                      color: 'white',
                      transform: desktopCollapsed ? 'scale(1.1)' : 'translateX(4px)',
                      boxShadow: `0 8px 25px rgba(63, 81, 181, 0.3), 
                                 0 4px 12px rgba(63, 81, 181, 0.2),
                                 inset 0 1px 0 rgba(255,255,255,0.1)`,
                      backdropFilter: 'blur(10px)',
                      '& .MuiListItemIcon-root': {
                        color: 'white',
                        transform: 'scale(1.1)',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                      },
                      '& .MuiListItemText-root': {
                        color: 'white',
                        fontWeight: 600
                      }
                    },
                    '&:hover:not(.Mui-selected)': {
                      background: 'rgba(63, 81, 181, 0.04)',
                      transform: desktopCollapsed ? 'scale(1.15)' : 'translateX(8px)',
                      boxShadow: '0 4px 12px rgba(63, 81, 181, 0.1)',
                      backdropFilter: 'blur(10px)',
                      '& .MuiListItemIcon-root': {
                        color: item.color,
                        transform: 'scale(1.15) rotate(5deg)',
                        filter: `drop-shadow(0 2px 4px ${item.color}40)`
                      }
                    }
                  }}
                >
                  <ListItemIcon 
                    sx={{ 
                      minWidth: desktopCollapsed ? 'auto' : 40,
                      mr: desktopCollapsed ? 0 : 1.5,
                      position: 'relative',
                      zIndex: 2,
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      color: isSelected ? 'white' : 'rgba(63, 81, 181, 0.7)'
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!desktopCollapsed && (
                    <ListItemText 
                      primary={item.text}
                      sx={{
                        position: 'relative',
                        zIndex: 2,
                        '& .MuiTypography-root': {
                          fontWeight: isSelected ? 600 : 500,
                          fontSize: '0.95rem',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }
                      }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>
      
      {/* Bouton nouvelle réunion - adapté selon le mode */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: 0, 
        width: '100%', 
        p: desktopCollapsed ? 1 : 2,
        background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.8) 100%)',
        backdropFilter: 'blur(10px)',
        zIndex: 1
      }}>
        {desktopCollapsed ? (
          <Tooltip title="Nouvelle réunion" placement="right">
            <IconButton
              onClick={handleNewMeeting}
              sx={{
                width: '100%',
                height: 48,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)',
                color: 'white',
                boxShadow: `0 6px 20px rgba(63, 81, 181, 0.25), 
                           0 3px 10px rgba(63, 81, 181, 0.15),
                           inset 0 1px 0 rgba(255,255,255,0.2)`,
                border: '1px solid rgba(255,255,255,0.1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 50%, #5C6BC0 100%)',
                  transform: 'translateY(-2px) scale(1.05)',
                  boxShadow: `0 8px 25px rgba(63, 81, 181, 0.35), 
                             0 4px 15px rgba(63, 81, 181, 0.25),
                             inset 0 1px 0 rgba(255,255,255,0.3)`,
                },
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            fullWidth
            onClick={handleNewMeeting}
            sx={{ 
              borderRadius: 3,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 50%, #7986CB 100%)',
              boxShadow: `0 6px 20px rgba(63, 81, 181, 0.25), 
                         0 3px 10px rgba(63, 81, 181, 0.15),
                         inset 0 1px 0 rgba(255,255,255,0.2)`,
              border: '1px solid rgba(255,255,255,0.1)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                transition: 'left 0.6s',
              },
              '&:hover': {
                background: 'linear-gradient(135deg, #303F9F 0%, #3F51B5 50%, #5C6BC0 100%)',
                transform: 'translateY(-2px) scale(1.01)',
                boxShadow: `0 8px 25px rgba(63, 81, 181, 0.35), 
                           0 4px 15px rgba(63, 81, 181, 0.25),
                           inset 0 1px 0 rgba(255,255,255,0.3)`,
                '&::before': {
                  left: '100%'
                },
                '& .MuiSvgIcon-root': {
                  transform: 'rotate(180deg) scale(1.05)'
                }
              },
              '& .MuiSvgIcon-root': {
                transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                fontSize: '1.1rem'
              },
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            Nouvelle réunion
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />
      
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { md: `${currentDrawerWidth}px` },
          height: '70px',
          background: isHomePage ? primaryGradient : primaryGradientTransparent,
          backdropFilter: isHomePage ? 'none' : 'blur(20px)',
          border: 'none',
          borderBottom: isHomePage ? 'none' : '1px solid rgba(255,255,255,0.1)',
          boxShadow: isHomePage 
            ? '0 4px 20px rgba(63, 81, 181, 0.3), 0 2px 10px rgba(63, 81, 181, 0.2)' 
            : `0 8px 32px rgba(63, 81, 181, 0.15), 
               0 4px 16px rgba(63, 81, 181, 0.1),
               inset 0 1px 0 rgba(255,255,255,0.1)`,
          zIndex: theme.zIndex.drawer + 1,
          transition: smoothTransition,
          '&::before': isHomePage ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0.05) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 1
          } : {}
        }}
      >
        <Toolbar 
          sx={{ 
            minHeight: '70px !important',
            height: '70px',
            maxHeight: '70px',
            padding: '0 24px',
            position: 'relative',
            zIndex: 2
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              borderRadius: 2,
              p: 1.5,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.2)',
                transform: 'scale(1.05)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              noWrap 
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: '1.3rem',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                background: 'linear-gradient(45deg, #ffffff 30%, rgba(255,255,255,0.8) 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {menuItems.find(item => isItemSelected(item))?.text || 'TranscriptionAI'}
            </Typography>
          </Box>

          {/* Icône de notifications */}
          <Tooltip title={unreadCount === 0 ? 'Aucune notification' : notificationsVisible ? `Masquer ${unreadCount} notification${unreadCount > 1 ? 's' : ''}` : `Afficher ${unreadCount} notification${unreadCount > 1 ? 's' : ''}`}>
            <IconButton
              onClick={handleNotificationsToggle}
              sx={{
                mr: 2,
                color: 'white',
                background: unreadCount > 0 && notificationsVisible
                  ? 'rgba(255,255,255,0.25)' 
                  : 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: `1px solid ${unreadCount > 0 && notificationsVisible
                  ? 'rgba(255,255,255,0.3)' 
                  : 'rgba(255,255,255,0.1)'}`,
                boxShadow: unreadCount > 0 && notificationsVisible
                  ? '0 4px 16px rgba(255,255,255,0.2)' 
                  : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-2px) scale(1.05)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
            >
              <Badge 
                badgeContent={unreadCount} 
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.7rem',
                    minWidth: 18,
                    height: 18,
                    backgroundColor: unreadCount > 0 && notificationsVisible ? '#FF5722' : '#FF9800',
                    boxShadow: unreadCount > 0 && notificationsVisible 
                      ? '0 4px 12px rgba(255, 87, 34, 0.6)' 
                      : '0 2px 8px rgba(255, 152, 0, 0.4)',
                    animation: unreadCount > 0 && !notificationsVisible 
                      ? 'notificationPulse 2s ease-in-out infinite' 
                      : 'none',
                    border: unreadCount > 0 && notificationsVisible 
                      ? '2px solid rgba(255,255,255,0.3)' 
                      : 'none',
                    '@keyframes notificationPulse': {
                      '0%, 100%': { 
                        transform: 'scale(1)',
                        boxShadow: '0 2px 8px rgba(255, 152, 0, 0.4)'
                      },
                      '50%': { 
                        transform: 'scale(1.15)',
                        boxShadow: '0 4px 16px rgba(255, 152, 0, 0.7)'
                      }
                    }
                  }
                }}
              >
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          {/* Bouton Supprimer toutes les notifications */}
          {unreadCount > 0 && (
            <Tooltip title={`Supprimer toutes les ${unreadCount} notification${unreadCount > 1 ? 's' : ''}`}>
              <IconButton
                onClick={handleDeleteAllNotifications}
                sx={{
                  mr: 1,
                  color: 'white',
                  background: 'rgba(244, 67, 54, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(244, 67, 54, 0.2)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    background: 'rgba(244, 67, 54, 0.2)',
                    transform: 'translateY(-2px) scale(1.05)',
                    boxShadow: '0 4px 16px rgba(244, 67, 54, 0.3)',
                    border: '1px solid rgba(244, 67, 54, 0.4)'
                  }
                }}
              >
                <DeleteSweepIcon sx={{ fontSize: '1.2rem' }} />
              </IconButton>
            </Tooltip>
          )}
          
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              cursor: 'pointer',
              borderRadius: 3,
              p: 1,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.1)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'rgba(255,255,255,0.2)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}
            onClick={handleProfileMenuOpen}
          >
            <Box sx={{ 
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: -2,
                right: -2,
                bottom: -2,
                background: 'linear-gradient(45deg, rgba(255,255,255,0.3), transparent, rgba(255,255,255,0.3))',
                borderRadius: '50%',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                zIndex: -1
              },
              '&:hover::after': {
                opacity: 1
              }
            }}>
              {getUserAvatar()}
            </Box>
            <Box sx={{ ml: 1.5, display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 500,
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }}
              >
                {getUserDisplayName()}
              </Typography>
              <ArrowDropDownIcon sx={{ 
                ml: 0.5,
                transition: 'transform 0.3s ease',
                transform: Boolean(anchorEl) ? 'rotate(180deg)' : 'rotate(0deg)'
              }} />
            </Box>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            sx={{
              '& .MuiPaper-root': {
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 8px 16px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                minWidth: 200,
                mt: 1
              }
            }}
          >
            <MenuItem 
              onClick={() => {
                handleProfileMenuClose();
                handleNavigate('/profile');
              }}
              sx={{
                py: 1.5,
                px: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, rgba(63, 81, 181, 0.05), rgba(92, 107, 192, 0.08))',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AccountCircleIcon 
                  fontSize="small" 
                  sx={{ color: 'rgba(63, 81, 181, 0.8)' }}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Mon profil" 
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 500
                  }
                }}
              />
            </MenuItem>
            
            {/* Indicateur de statut de connexion */}
            <ConnectionStatusMenuItem onMenuClose={handleProfileMenuClose} />
            
            <MenuItem 
              onClick={() => {
                handleProfileMenuClose();
                handleNavigate('/changelog');
              }}
              sx={{
                py: 1.5,
                px: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, rgba(255, 152, 0, 0.05), rgba(255, 152, 0, 0.08))',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <UpdateIcon 
                  fontSize="small" 
                  sx={{ color: 'rgba(255, 152, 0, 0.8)' }}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Changelog" 
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 500
                  }
                }}
              />
            </MenuItem>
            <MenuItem 
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'linear-gradient(45deg, rgba(244, 67, 54, 0.05), rgba(244, 67, 54, 0.08))',
                  transform: 'translateX(4px)'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon 
                  fontSize="small" 
                  sx={{ color: 'rgba(244, 67, 54, 0.8)' }}
                />
              </ListItemIcon>
              <ListItemText 
                primary="Déconnexion" 
                sx={{
                  '& .MuiTypography-root': {
                    fontWeight: 500
                  }
                }}
              />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ 
          width: { md: currentDrawerWidth }, 
          flexShrink: { md: 0 }
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth, // Mobile garde toujours la largeur complète
              border: 'none',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: currentDrawerWidth,
              border: 'none',
              boxShadow: '0 20px 40px rgba(0,0,0,0.05)',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${currentDrawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          position: 'relative',
          paddingTop: '70px',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          // Effet de fond différent selon la page
          '&::before': {
            content: '""',
            position: 'fixed',
            top: 70,
            left: { md: currentDrawerWidth, xs: 0 },
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.01) 0%, rgba(92, 107, 192, 0.01) 50%, rgba(121, 134, 203, 0.01) 100%)',
            pointerEvents: 'none',
            zIndex: -1,
            transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }
        }}
      >
        <Toolbar sx={{ minHeight: '0px !important' }} />
        {children}
      </Box>

      <NewMeetingDialog
        open={newMeetingDialogOpen}
        onClose={() => setNewMeetingDialogOpen(false)}
        onSuccess={handleNewMeetingSuccess}
      />
      
      {/* Centre de notifications */}
      <NotificationCenter isVisible={notificationsVisible} />
      
      {/* Composant de notification non-invasif pour remplacer les alert() */}
      <SessionWarning />
      
      {/* Écouteur pour les notifications d'authentification */}
      <AuthNotificationListener />
    </Box>
  );
};

export default MainLayout; 